/**
 * demoBackend — an in-browser stand-in for the server's REST API in the
 * simulated build. It owns an evolving world model (runs + cumulative stats)
 * and answers the `/api/*` routes the React Query hooks call. DemoSimulation
 * mutates it through the write methods; `demoRequest()` reads fresh snapshots.
 *
 * Agent-level detail is intentionally omitted (`/api/agents` returns []): the
 * scene gets agent names/backends from the topology definition, and cost/token
 * totals are tracked per-run and globally — nothing reads per-agent runtime.
 */

import type { WorldInfo, WorldStats, TopologyInfo, RunInfo } from "../api/client.js";
import type { TopologyInfo as SceneTopology } from "../pixel-world/scenes/SceneLayout.js";
import { DEMO_TOPOLOGIES, demoTopologyDefinition } from "./topologies.js";

const RUN_INPUTS = [
  "Summarize the Q3 earnings call",
  "Triage incoming support tickets",
  "Research competitor pricing",
  "Draft the launch announcement",
  "Analyze user churn signals",
  "Reconcile the monthly invoices",
  "Plan the next sprint backlog",
  "Review the open pull request",
  "Generate the weekly metrics report",
  "Investigate the latency regression",
  "Compile the customer feedback digest",
  "Audit access permissions",
];

let runSeq = 1000;
function nextRunId(): string {
  return `run-${(runSeq++).toString(36)}`;
}

// Rotate through the input pool so consecutive runs never repeat a prompt.
let inputSeq = 0;
function nextInput(): string {
  return RUN_INPUTS[inputSeq++ % RUN_INPUTS.length];
}

interface Cumulative {
  totalRuns: number;
  completedRuns: number;
  failedRuns: number;
  totalCostUsd: number;
  tokensIn: number;
  tokensOut: number;
}

class DemoBackend {
  private runs = new Map<string, RunInfo[]>();
  private cumulative: Cumulative = {
    totalRuns: 0,
    completedRuns: 0,
    failedRuns: 0,
    totalCostUsd: 0,
    tokensIn: 0,
    tokensOut: 0,
  };
  private readonly maxRunsPerTopology = 8;

  constructor() {
    const now = Date.now();
    for (const meta of DEMO_TOPOLOGIES) {
      const def = demoTopologyDefinition(meta.id);
      const seeded: RunInfo[] = [];

      // History: a couple completed + one failed, so the overlays read as busy.
      const history: RunInfo["status"][] = ["completed", "completed", "failed"];
      history.forEach((status, i) => {
        const startedAt = now - (i + 2) * 70_000 - Math.floor(Math.random() * 30_000);
        const run = this.makeRun(meta.id, def, status, startedAt);
        run.completedAt = startedAt + 25_000 + Math.floor(Math.random() * 40_000);
        run.output =
          status === "failed" ? "Run failed — see activity log." : "Run completed successfully.";
        this.seedTotals(
          run,
          round4(0.02 + Math.random() * 0.2),
          1500 + Math.floor(Math.random() * 6000),
          400 + Math.floor(Math.random() * 2000),
        );
        seeded.push(run);
      });

      // Two in-flight runs with a little progress already on the clock.
      for (let i = 0; i < 2; i++) {
        const run = this.makeRun(meta.id, def, "running", now - Math.floor(Math.random() * 25_000));
        this.seedTotals(
          run,
          round4(0.004 + Math.random() * 0.03),
          300 + Math.floor(Math.random() * 1500),
          120 + Math.floor(Math.random() * 600),
        );
        seeded.push(run);
      }

      this.runs.set(meta.id, seeded);
    }
    this.cumulative.totalCostUsd = round4(this.cumulative.totalCostUsd);
  }

  private seedTotals(run: RunInfo, cost: number, tokensIn: number, tokensOut: number): void {
    run.totalCostUsd = cost;
    run.totalTokens = { input: tokensIn, output: tokensOut, cached: 0 };
    this.cumulative.totalRuns++;
    if (run.status === "completed") this.cumulative.completedRuns++;
    else if (run.status === "failed") this.cumulative.failedRuns++;
    this.cumulative.totalCostUsd += cost;
    this.cumulative.tokensIn += tokensIn;
    this.cumulative.tokensOut += tokensOut;
  }

  private makeRun(
    topologyId: string,
    def: SceneTopology,
    status: RunInfo["status"],
    startedAt: number,
  ): RunInfo {
    return {
      id: nextRunId(),
      topologyInstanceId: topologyId,
      status,
      input: nextInput(),
      supervised: false,
      totalCostUsd: 0,
      totalTokens: { input: 0, output: 0, cached: 0 },
      pendingInputs: [],
      entryAgent: def.entryPoint,
      participatingAgents: def.agents.map((a) => a.id),
      startedAt,
    };
  }

  // ── Reads (REST) ──────────────────────────────────────────

  getWorld(): WorldInfo {
    const all = [...this.runs.values()].flat();
    return {
      status: "running",
      topologyCount: DEMO_TOPOLOGIES.length,
      runCount: this.cumulative.totalRuns,
      activeRunCount: all.filter((r) => r.status === "running").length,
      topologies: DEMO_TOPOLOGIES.map((m) => ({
        id: m.id,
        type: m.type,
        status: "active",
        runCount: this.runs.get(m.id)?.length ?? 0,
      })),
    };
  }

  getStats(): WorldStats {
    const c = this.cumulative;
    return {
      status: "running",
      totalRuns: c.totalRuns,
      completedRuns: c.completedRuns,
      failedRuns: c.failedRuns,
      totalCostUsd: round4(c.totalCostUsd),
      totalTokens: { input: c.tokensIn, output: c.tokensOut },
    };
  }

  listTopologies(): TopologyInfo[] {
    return DEMO_TOPOLOGIES.map((m) => this.getTopology(m.id));
  }

  getTopology(id: string): TopologyInfo {
    const def = demoTopologyDefinition(id);
    const now = Date.now();
    return {
      id,
      definition: def,
      status: "active",
      persistentAgentIds: def.agents.map((a) => a.id),
      runCount: this.runs.get(id)?.length ?? 0,
      createdAt: now - 3_600_000,
      updatedAt: now,
    };
  }

  listRuns(topologyId?: string): RunInfo[] {
    const source = topologyId
      ? (this.runs.get(topologyId) ?? [])
      : [...this.runs.values()].flat();
    // Return fresh clones so React Query sees evolving values.
    return source.map((r) => ({ ...r, totalTokens: { ...r.totalTokens } }));
  }

  getRun(id: string): RunInfo | undefined {
    for (const list of this.runs.values()) {
      const found = list.find((r) => r.id === id);
      if (found) return { ...found, totalTokens: { ...found.totalTokens } };
    }
    return undefined;
  }

  // ── Writes (DemoSimulation) ───────────────────────────────

  /**
   * Keep ~2 runs in flight per topology (so the ACTIVE stat reads busy) and
   * return the one to advance this tick.
   */
  ensureActiveRun(topologyId: string): RunInfo {
    const list = this.runs.get(topologyId) ?? [];
    const active = list.filter((r) => r.status === "running");
    while (active.length < 2) active.push(this.startRun(topologyId));
    return active[0];
  }

  startRun(topologyId: string): RunInfo {
    const def = demoTopologyDefinition(topologyId);
    const run = this.makeRun(topologyId, def, "running", Date.now());
    const list = this.runs.get(topologyId) ?? [];
    list.push(run);
    if (list.length > this.maxRunsPerTopology) list.shift();
    this.runs.set(topologyId, list);
    this.cumulative.totalRuns++;
    return run;
  }

  /** Accrue work onto a run and the global totals. */
  bumpRun(run: RunInfo, delta: { cost: number; tokensIn: number; tokensOut: number }): void {
    run.totalCostUsd = round4(run.totalCostUsd + delta.cost);
    run.totalTokens.input += delta.tokensIn;
    run.totalTokens.output += delta.tokensOut;
    this.cumulative.totalCostUsd += delta.cost;
    this.cumulative.tokensIn += delta.tokensIn;
    this.cumulative.tokensOut += delta.tokensOut;
  }

  completeRun(run: RunInfo, failed: boolean): void {
    run.status = failed ? "failed" : "completed";
    run.completedAt = Date.now();
    run.output = failed ? "Run failed — see activity log." : "Run completed successfully.";
    if (failed) this.cumulative.failedRuns++;
    else this.cumulative.completedRuns++;
  }
}

function round4(n: number): number {
  return Math.round(n * 10_000) / 10_000;
}

export const demoBackend = new DemoBackend();

/** Route an intercepted REST call to the in-memory model. */
export async function demoRequest<T>(method: string, path: string, _body?: unknown): Promise<T> {
  const url = new URL(path, "http://demo.local");
  const segs = url.pathname.replace(/^\/api\/?/, "").split("/").filter(Boolean);
  const topologyId = url.searchParams.get("topologyId") ?? undefined;

  if (method === "GET") {
    const [a, b] = segs;
    if (a === "world") return (b === "stats" ? demoBackend.getStats() : demoBackend.getWorld()) as T;
    if (a === "topologies") return (b ? demoBackend.getTopology(b) : demoBackend.listTopologies()) as T;
    if (a === "runs") {
      if (b) return demoBackend.getRun(b) as T;
      return demoBackend.listRuns(topologyId) as T;
    }
    // agents, context, feedback, and anything else: empty list is a safe default.
    return [] as unknown as T;
  }

  // Mutations have no effect in the simulated build.
  return undefined as unknown as T;
}
